import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { User } from '../types';

interface AssignUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (user: User) => void;
  apiBaseUrl: string;
}

const AssignUserModal: React.FC<AssignUserModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  apiBaseUrl,
}) => {
  const [searchId, setSearchId] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const params: Record<string, string | number> = { page: 1, limit: 20 };

      if (searchId.trim()) params.id = searchId.trim();
      if (searchEmail.trim()) params.email = searchEmail.trim();
      if (searchFirstName.trim()) params.firstName = searchFirstName.trim();
      if (searchLastName.trim()) params.lastName = searchLastName.trim();

      const response = await axios.get(`${apiBaseUrl}/users`, {
        withCredentials: true,
        params,
      });

      setUsers(response.data.users);
      if (response.data.users.length === 0) {
        setError('No users found matching your search');
      }
    } catch (err: any) {
      console.error('Error searching users:', err);
      setError(err.response?.data?.message || 'Failed to search users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (user: User) => {
    setAssigningId(user._id);
    setError(null);
    try {
      await onAssign(user);
      setSearchId('');
      setSearchEmail('');
      setSearchFirstName('');
      setSearchLastName('');
      setUsers([]);
      setSearched(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign device');
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Assign Device to User</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            <form onSubmit={handleSearch} className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="ID"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                style={{ width: '120px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                style={{ width: '160px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="First Name"
                value={searchFirstName}
                onChange={(e) => setSearchFirstName(e.target.value)}
                style={{ width: '120px' }}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Last Name"
                value={searchLastName}
                onChange={(e) => setSearchLastName(e.target.value)}
                style={{ width: '120px' }}
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                Search
              </button>
            </form>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            {loading ? (
              <div className="d-flex justify-content-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : searched && users.length > 0 ? (
              <table className="table table-sm table-striped mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAssign(user)}
                          disabled={assigningId === user._id}
                        >
                          {assigningId === user._id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                              Assign
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : searched && !error ? (
              <p className="text-muted mb-0">No users found.</p>
            ) : (
              <p className="text-muted mb-0">Search for a user to assign this device.</p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignUserModal;
